import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации диапазона дат в виде календаря
 */
const CalendarVisualization = ({ 
  dateRange, 
  startDate,
  endDate,
  holidays = [],
  customHolidays = [],
  workingDaysOnly = false
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [visibleDates, setVisibleDates] = useState([]);
  const [legendVisible, setLegendVisible] = useState(true);

  // Эффект при изменении параметров
  useEffect(() => {
    if (dateRange && dateRange.length > 0) {
      // Устанавливаем текущий месяц и год на дату начала диапазона
      const firstDate = new Date(startDate);
      setCurrentMonth(new Date(firstDate.getFullYear(), firstDate.getMonth(), 1));
      setCurrentYear(firstDate.getFullYear());
    }
  }, [dateRange, startDate]);

  // Эффект для генерации видимых дат текущего месяца
  useEffect(() => {
    if (!currentMonth) return;

    // Получаем первый день месяца
    const firstDayOfMonth = new Date(currentYear, currentMonth.getMonth(), 1);
    
    // Получаем последний день месяца
    const lastDayOfMonth = new Date(currentYear, currentMonth.getMonth() + 1, 0);
    
    // Получаем номер дня недели первого дня месяца (0 - воскресенье, 1 - понедельник и т.д.)
    let firstDayOfWeek = firstDayOfMonth.getDay();
    // Корректируем для начала недели с понедельника
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    // Генерируем массив дат для отображения в календаре
    const dates = [];
    
    // Добавляем дни предыдущего месяца
    const prevMonthLastDay = new Date(currentYear, currentMonth.getMonth(), 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(currentYear, currentMonth.getMonth() - 1, day);
      dates.push({
        date,
        day,
        isCurrentMonth: false,
        isSelected: isDateInRange(date),
        isStart: isSameDate(date, new Date(startDate)),
        isEnd: isSameDate(date, new Date(endDate)),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isHoliday: isHoliday(date),
        isWorkingDay: isWorkingDay(date)
      });
    }
    
    // Добавляем дни текущего месяца
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(currentYear, currentMonth.getMonth(), day);
      dates.push({
        date,
        day,
        isCurrentMonth: true,
        isSelected: isDateInRange(date),
        isStart: isSameDate(date, new Date(startDate)),
        isEnd: isSameDate(date, new Date(endDate)),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isHoliday: isHoliday(date),
        isWorkingDay: isWorkingDay(date)
      });
    }
    
    // Добавляем дни следующего месяца для заполнения сетки календаря
    const daysToAdd = 42 - dates.length;
    for (let day = 1; day <= daysToAdd; day++) {
      const date = new Date(currentYear, currentMonth.getMonth() + 1, day);
      dates.push({
        date,
        day,
        isCurrentMonth: false,
        isSelected: isDateInRange(date),
        isStart: isSameDate(date, new Date(startDate)),
        isEnd: isSameDate(date, new Date(endDate)),
        isWeekend: date.getDay() === 0 || date.getDay() === 6,
        isHoliday: isHoliday(date),
        isWorkingDay: isWorkingDay(date)
      });
    }
    
    setVisibleDates(dates);
  }, [currentMonth, currentYear, dateRange, startDate, endDate, holidays, customHolidays, workingDaysOnly]);

  // Проверяет, находится ли дата в выбранном диапазоне
  const isDateInRange = (date) => {
    if (!dateRange || dateRange.length === 0) return false;
    
    return dateRange.some(rangeDate => 
      isSameDate(rangeDate, date)
    );
  };

  // Проверяет, является ли дата выходным днем
  const isWorkingDay = (date) => {
    if (!workingDaysOnly) return false;
    
    // Проверяем, что это рабочий день (пн-пт) и не праздник
    const isWeekday = date.getDay() !== 0 && date.getDay() !== 6;
    return isWeekday && !isHoliday(date) && isDateInRange(date);
  };

  // Проверяет, является ли дата праздником
  const isHoliday = (date) => {
    if (!date) return false;
    
    const dateStr = date.toISOString().split('T')[0];
    
    // Проверяем среди основных и пользовательских праздников
    return [...holidays, ...customHolidays].some(holiday => {
      const holidayDate = typeof holiday === 'string' 
        ? holiday 
        : holiday.date;
      return holidayDate === dateStr;
    });
  };

  // Проверяет, является ли две даты одинаковыми (игнорируя время)
  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    
    return date1.getFullYear() === date2.getFullYear() && 
           date1.getMonth() === date2.getMonth() && 
           date1.getDate() === date2.getDate();
  };

  // Переход к предыдущему месяцу
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentYear, currentMonth.getMonth() - 1, 1));
    
    // Обновляем год если нужно
    if (currentMonth.getMonth() === 0) {
      setCurrentYear(currentYear - 1);
    }
  };

  // Переход к следующему месяцу
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentYear, currentMonth.getMonth() + 1, 1));
    
    // Обновляем год если нужно
    if (currentMonth.getMonth() === 11) {
      setCurrentYear(currentYear + 1);
    }
  };

  // Названия дней недели
  const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Названия месяцев
  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

  if (!dateRange || dateRange.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-cyan-500" />
          Визуализация дат
        </h3>
        
        {/* Переключатель легенды */}
        <button
          type="button"
          onClick={() => setLegendVisible(prev => !prev)}
          className="text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 flex items-center"
        >
          <Info className="w-4 h-4 mr-1" />
          {legendVisible ? 'Скрыть легенду' : 'Показать легенду'}
        </button>
      </div>

      {/* Легенда */}
      {legendVisible && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex flex-wrap gap-3 text-xs">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-cyan-100 dark:bg-cyan-800/40 rounded mr-2"></div>
            <span className="text-gray-700 dark:text-gray-300">Выбранный день</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-50 dark:bg-red-900/20 rounded mr-2"></div>
            <span className="text-gray-700 dark:text-gray-300">Выходной</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-orange-50 dark:bg-orange-900/20 rounded mr-2"></div>
            <span className="text-gray-700 dark:text-gray-300">Праздник</span>
          </div>
          {workingDaysOnly && (
            <div className="flex items-center">
              <div className="w-4 h-4 bg-emerald-50 dark:bg-emerald-900/20 rounded mr-2"></div>
              <span className="text-gray-700 dark:text-gray-300">Рабочий день</span>
            </div>
          )}
          <div className="flex items-center ml-auto">
            <span className="text-gray-500 dark:text-gray-400">Период: {formatDate(new Date(startDate), 'short')} - {formatDate(new Date(endDate), 'short')}</span>
          </div>
        </div>
      )}

      {/* Навигация по месяцам */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={goToPreviousMonth}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
          aria-label="Предыдущий месяц"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">
          {monthNames[currentMonth.getMonth()]} {currentYear}
        </h4>
        
        <button
          type="button"
          onClick={goToNextMonth}
          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
          aria-label="Следующий месяц"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Сетка календаря */}
      <div className="calendar-grid mb-2">
        {/* Дни недели */}
        {weekDays.map((day, index) => (
          <div 
            key={`weekday-${index}`} 
            className="text-sm font-medium text-gray-500 dark:text-gray-400 text-center py-1"
          >
            {day}
          </div>
        ))}
        
        {/* Дни месяца */}
        {visibleDates.map((dateObj, index) => (
          <div 
            key={`date-${index}`} 
            className={`calendar-day text-sm relative
              ${!dateObj.isCurrentMonth ? 'text-gray-400 dark:text-gray-600' : 'text-gray-800 dark:text-gray-200'}
              ${dateObj.isWeekend ? 'weekend' : ''}
              ${dateObj.isHoliday ? 'holiday' : ''}
              ${dateObj.isSelected && !dateObj.isStart && !dateObj.isEnd ? 'selected' : ''}
              ${dateObj.isStart ? 'start' : ''}
              ${dateObj.isEnd ? 'end' : ''}
              ${dateObj.isWorkingDay ? 'working-day' : ''}
            `}
          >
            <span className="relative z-10">{dateObj.day}</span>
            
            {/* Индикатор сегодняшнего дня */}
            {isSameDate(dateObj.date, new Date()) && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-6 h-6 border-2 border-cyan-500 dark:border-cyan-400 rounded-full"></div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Информация о визуализации */}
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-3">
        <p>* Показаны месяцы, входящие в выбранный период дат. Используйте стрелки для навигации.</p>
      </div>
    </div>
  );
};

export default CalendarVisualization;